import * as t from 'io-ts';

import { checkIfServerIsDown } from '../../actions/AccountActions/Helper';
import { sessionExpire } from '../../actions/AuthenticationActions';
import { addFlashMessages } from '../../actions/FlashMessagesActions';
import { abortRequest, emptyRequest, retryAfterRequest } from '../../actions/GenericActions';
import { APP_EMAIL, ENDPOINT_VERSION } from '../../config';
import { localeData } from '../../locales/data';
import { SUPPORT_URL_PLACEHOLDER } from '../constants';
import { logErrorService } from '../services';

import { validReporter } from './io-ts-helpers';

export const fetchRequestDataWrapper = (response: any) => {
  if (response.ok) {
    return response.json();
  } else {
    logErrorService(response.status, response.json());
  }
};

export const webSimApiErrorFlashMessages = (dispatch: any, context: string = 'generic', location?: string) =>
  dispatch(
    addFlashMessages([{ msg: 'api.error.brain.experiencing', defaultMessage: localeData.en['api.error.brain.experiencing'] }], {
      links: [
        {
          urls: [SUPPORT_URL_PLACEHOLDER],
          keys: ['api.error.brain.experiencing.link'],
          defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
        },
      ],
      context,
      location,
    })
  );

export const defaultErrorMessage = (dispatch: any, location: string = 'top') => webSimApiErrorFlashMessages(dispatch, 'generic', location);

/**
 * @param key a key from data.ts referencing the desired message
 * @param options see {@link BaseFlashMessageOptionsStore}
 */
export const addFlashMessageWrapper = (key: string, options?: any) => ({
  payload: {
    message: [{ defaultMessage: localeData.en[key], msg: key }],
    options,
  },
  type: 'ADD_FLASH_MESSAGES',
});

export const ioTsValidationError = (json: any, validator: any) => {
  const validation = t.exact(validator).decode(json);
  const isValid: any = validReporter.report(validation);

  if (typeof isValid === 'object') {
    Object.keys(isValid).map((elem: any) => {
      if (isValid.hasOwnProperty(elem)) {
        logErrorService(isValid[elem].errors, isValid[elem].errors);
      }
    });
  }
};

export interface GlobalFetchOptions {
  answerEmpty?: any;
  callbackAction?: () => any;
  callbackRedirect?: (a: any) => any;
  callbackStatus400?: any;
  callbackStatus401?: any;
  callbackStatus404?: any;
  callbackStatus429?: () => any;
  contactSupportMessage?: any;
  endpointVersion?: string;
  hasAbortError?: boolean;
  isResponseJson?: boolean;
  retryEmpty?: any;
}

export async function getResponseContent(response: Response, isJson: boolean) {
  if (!response.json && !response.text) {
    return Promise.resolve('');
  }
  if (isJson) {
    return response.json();
  }

  return response.text();
}

export const globalFetch = async (
  url: any,
  urlOptions: any,
  dispatch: any,
  failureCallback: any,
  {
    answerEmpty,
    callbackAction,
    callbackRedirect,
    callbackStatus400,
    callbackStatus401,
    callbackStatus404,
    callbackStatus429,
    contactSupportMessage,
    endpointVersion = ENDPOINT_VERSION,
    hasAbortError = false,
    isResponseJson = true,
    retryEmpty,
  }: GlobalFetchOptions = {}
) => {
  const headers = urlOptions.headers ? urlOptions.headers : new Headers();
  headers.append('Accept', `application/json;${endpointVersion}`);

  return fetch(url, { ...urlOptions, headers })
    .then(async (response: any): Promise<object> => {
      if (response.redirected && typeof callbackRedirect === 'function') {
        return callbackRedirect(response);
      }
      if (response.ok && response.status !== 204) {
        const timeOut = response.headers.get('Retry-After');
        if (timeOut !== null && callbackAction && typeof callbackAction === 'function') {
          setTimeout(() => {
            callbackAction();
          }, parseFloat(timeOut) * 1000);
          if (retryEmpty) {
            return dispatch(retryAfterRequest());
          } else {
            return getResponseContent(response, isResponseJson);
          }
        } else {
          if (answerEmpty) {
            return dispatch(emptyRequest());
          } else {
            return getResponseContent(response, isResponseJson);
          }
        }
      } else if (response.status === 204) {
        return dispatch(emptyRequest());
      } else if (response.status === 404) {
        if (callbackStatus404 !== undefined && typeof callbackStatus404 === 'function') {
          return callbackStatus404(response);
        } else {
          return failureCallback();
        }
      } else if (response.status === 401) {
        logErrorService(response.statusText, response.status);

        if (callbackStatus401 !== undefined && typeof callbackStatus401 === 'function') {
          callbackStatus401();

          return failureCallback();
        } else {
          dispatch(addFlashMessages([{ msg: 'session.expire.message', defaultMessage: localeData.en['session.expire.message'] }]));
          dispatch(sessionExpire());

          return failureCallback();
        }
      } else if (response.status === 429) {
        logErrorService(response.statusText, response.status);

        if (callbackStatus429) {
          callbackStatus429();
        }

        return failureCallback();
      } else if (response.status >= 500 && response.status <= 504) {
        return checkIfServerIsDown(dispatch, response, failureCallback);
      } else if (response.status === 400) {
        logErrorService(response.statusText, response.status);
        if (callbackStatus400 !== undefined && typeof callbackStatus400 === 'function') {
          return callbackStatus400(response);
        } else {
          return failureCallback();
        }
      } else {
        try {
          return getResponseContent(response, isResponseJson);
        } catch (e) {
          logErrorService(response.statusText, response.status);
          if (contactSupportMessage && typeof contactSupportMessage === 'function') {
            contactSupportMessage();
          }

          return failureCallback();
        }
      }
    })
    .catch((e: any) => {
      if (e.name !== 'AbortError' && e.code !== 20) {
        logErrorService(e, e);
        webSimApiErrorFlashMessages(dispatch);

        return failureCallback();
      } else {
        if (hasAbortError) {
          return dispatch(abortRequest());
        } else {
          throw e;
        }
      }
    });
};

export const cacheValidityPeriod = 6 * 60 * 60 * 1000; //6 hrs
interface CacheEntry {
  date: number;
  returnValue: Promise<any>;
}
//https://github.com/aldrinpvincent/memoize-promise-fn/blob/main/src/index.ts with cache validity period
export const memoizePromiseFn = (fn: (...arg: any[]) => Promise<any>) => {
  const cache = new Map<string, CacheEntry>();

  return (...args: any) => {
    const context = this;
    const key = JSON.stringify(args);

    if (cache.has(key)) {
      const foundValue = cache.get(key);
      if (foundValue?.date && new Date().getTime() - new Date(foundValue.date).getTime() < cacheValidityPeriod) {
        return foundValue.returnValue;
      }
    }

    const returnValue = fn.apply(context, args).catch((error: any) => {
      // Delete cache entry if api call fails
      cache.delete(key);
      return Promise.reject(error);
    });

    const date = new Date().getTime();
    const storedValue = { date, returnValue };

    cache.set(key, storedValue);

    return returnValue;
  };
};

//todo: replace by queryClient.fetchQuery after merging Data Explorer
export const cachedFetch = memoizePromiseFn(globalFetch);

export const sendFlashMessageEmail = (dispatch: any, error: any, userId: any = undefined) => {
  const mailRef = `"mailto:${APP_EMAIL}?subject=An Application Error Has Occurred For ${userId || 'Not Signed In User'}"`;
  const errorMessage =
    'WorldQuant BRAIN is experiencing some difficulties. ' +
    `Please contact <a className="error-boundary__element-link" href=${mailRef}>support</a > if this problem persists.`;
  logErrorService(errorMessage, error);

  return dispatch(addFlashMessages([{ msg: errorMessage, defaultMessage: localeData.en[errorMessage], options: { parseHtml: true } }]));
};

export const specialCasesCorrelation = async ({ response, dispatch, context, dispatchFailure, location = 'simulation-alphas' }: any) => {
  if (response.status === 401) {
    logErrorService(response.statusText, response.status);
    await dispatch(sessionExpire());

    return dispatchFailure();
  } else if (response.status === 410 || response.status === 412) {
    return dispatchFailure();
  } else if (response.status === 429) {
    dispatch(
      addFlashMessages(
        [
          {
            msg: 'alphas.correlation.too.many.requests',
            defaultMessage: localeData.en['alphas.correlation.too.many.requests'],
          },
        ],
        {
          context,
          location,
        }
      )
    );

    return dispatchFailure();
  } else if (response.status === 503) {
    dispatch(
      addFlashMessages(
        [
          {
            msg: 'alphas.correlation.unavailable',
            defaultMessage: localeData.en['alphas.correlation.unavailable'],
          },
        ],
        {
          links: [
            {
              urls: [SUPPORT_URL_PLACEHOLDER],
              keys: ['api.error.brain.experiencing.link'],
              defaultMsgkeys: [localeData.en['api.error.brain.experiencing.link']],
            },
          ],
          location,
          context,
        }
      )
    );

    return dispatchFailure();
  }
};

export const isAction = (json: any, action: string) =>
  json && (json.type === action || json.type === 'INVALID_REQUEST' || json.type === 'ABORT_REQUEST' || json.type === 'RETRY_AFTER_REQUEST');
